import { create } from 'zustand';

const useChallengeStore = create((set) => ({
    // Dati generici della challenge (Pagina 1)
    challengeId: null, // ID della challenge salvata (null se non ancora salvata)
    challengeTitle: '',
    challengeDescription: '',
    challengeLearningTopic: '',
    learningTopicLevel: 1,
    isValidation: false,
    isForTeam: true,
    challengeDifficulty: 'Easy',
    challengeTags: '',
    averageTime: '',
    challengeLinkDemopath: '',
    timerEnabled: false, // true = Incremental Timer, false = No Timer or Countdown Timer
    timerType: 'noTimer', // UI selection: 'noTimer', 'incremental', or 'countdown'
    isQualificationSkill: false,

    // Step della challenge (Pagina 2)
    steps: [],

    // Stato di pubblicazione
    isDraft: true,

    // Pagina corrente
    currentPage: 1,

    // Actions per aggiornare i dati generici
    updateChallengeInfo: (field, value) => set(() => ({
        [field]: value
    })),

    // Actions per gestire gli step
    addStep: () => set((state) => ({
        steps: [...state.steps, {
            id: `step-${Date.now()}`,
            questionType: 'openField', // Default to openField
            questionField: '', // Required
            imageLink: '',
            stepScorePassed: '5',
            stepScoreFailed: '0',
            stepMetaSkill: '',
            stepHardSkill: '',
            countdownTime: '',
            isBlockedStep: false,
            evaluationCriteria: false,
            skillDimensionRef: null,
            target: 'button', // Always 'button' for API
            parameter: ['next'], // Always ['next'] for API
            // Campi specifici per tipo
            placeholder: '',
            type: 'text',
            validationField: '',
            answers: {},
            reportDownloadLink: '',
            videoLink: '',
            randomizeAnswer: false
        }]
    })),

    removeStep: (stepId) => set((state) => ({
        steps: state.steps.filter(step => step.id !== stepId)
    })),

    updateStep: (stepId, field, value) => set((state) => ({
        steps: state.steps.map(step =>
            step.id === stepId
                ? { ...step, [field]: value }
                : step
        )
    })),

    reorderSteps: (newSteps) => set({ steps: newSteps }),

    // Navigation
    setCurrentPage: (page) => set({ currentPage: page }),

    nextPage: () => set((state) => ({
        currentPage: Math.min(state.currentPage + 1, 3)
    })),

    previousPage: () => set((state) => ({
        currentPage: Math.max(state.currentPage - 1, 1)
    })),

    // Pubblica challenge
    togglePublish: () => set((state) => ({
        isDraft: !state.isDraft
    })),

    // Ottieni i dati formattati per il BE
    getChallengeData: () => {
        const state = useChallengeStore.getState();

        // Build blockedSteps array with indices of blocked steps
        const blockedSteps = state.steps
            .map((step, index) => step.isBlockedStep ? index : null)
            .filter(index => index !== null);

        const isQualificationSkill = !!(state.steps[0]?.skillDimensionRef);

        return {
            challengeTitle: state.challengeTitle,
            challengeDescription: state.challengeDescription,
            challengeLearningTopic: state.challengeLearningTopic,
            learningTopicLevel: state.learningTopicLevel,
            isValidation: state.isValidation,
            isForTeam: state.isForTeam,
            challengeDifficulty: state.challengeDifficulty,
            challengeTags: state.challengeTags,
            averageTime: state.averageTime,
            challengeLinkDemopath: state.challengeLinkDemopath,
            timerEnabled: state.timerEnabled,
            timerType: state.timerType,
            isDraft: state.isDraft,
            isQualificationSkill: isQualificationSkill,
            blockedSteps: blockedSteps,
            steps: state.steps.map((step, index) => {
                const { isBlockedStep, id, ...stepData } = step;
                return {
                    step: String(index + 1),
                    ...stepData
                };
            })
        };
    },

    // Reset completo
    reset: () => set({
        challengeId: null,
        challengeTitle: '',
        challengeDescription: '',
        challengeLearningTopic: '',
        learningTopicLevel: 1,
        isValidation: false,
        isForTeam: true,
        challengeDifficulty: 'Easy',
        challengeTags: '',
        averageTime: '',
        challengeLinkDemopath: '',
        timerEnabled: false,
        timerType: 'noTimer',
        isQualificationSkill: false,
        steps: [],
        isDraft: true,
        currentPage: 1
    }),

    // Carica dati esistenti
    loadChallengeData: (data) => {
        // Determine timerType based on available data
        let timerType = data.timerType;

        if (!timerType) {
            // timerType not present in API response, derive it
            if (data.timerEnabled === true) {
                // timerEnabled true = incremental
                timerType = 'incremental';
            } else {
                // timerEnabled false - check if any step has countdownTime
                const hasCountdownTime = (data.steps || []).some(step =>
                    step.countdownTime && step.countdownTime !== ''
                );
                timerType = hasCountdownTime ? 'countdown' : 'noTimer';
            }
        }

        set({
            challengeId: data.challengeId || null,
            challengeTitle: data.challengeTitle || '',
            challengeDescription: data.challengeDescription || '',
            challengeLearningTopic: data.challengeLearningTopic || '',
            learningTopicLevel: data.learningTopicLevel || 1,
            isValidation: data.isValidation || false,
            isForTeam: data.isForTeam || false,
            challengeDifficulty: data.challengeDifficulty || 'Easy',
            challengeTags: data.challengeTags || '',
            averageTime: data.averageTime || '',
            challengeLinkDemopath: data.challengeLinkDemopath || '',
            timerEnabled: data.timerEnabled || false,
            timerType: timerType,
            isQualificationSkill: data.isQualificationSkill ?? false,
            steps: (data.steps || []).map((step, index) => ({
                ...step,
                id: step.id || `step-${Date.now()}-${index}`, // Ensure each step has an ID
                isBlockedStep: (data.blockedSteps || []).includes(index),
                evaluationCriteria: step.evaluationCriteria ?? false,
                skillDimensionRef: step.skillDimensionRef ?? null
            })),
            isDraft: data.isDraft || false
        });
    }
}));

export default useChallengeStore;
